/**
 * "Report a wrong word" on the web consoles — docs/31-global-languages.md.
 *
 * The phone's sheet, simplified to the page: a small link in the footer,
 * shown when the signed-in person's language is not English. It lists the
 * strings of their language that appear on the page now, and a search over
 * all of them; they pick one, may write a better wording and a short note,
 * and send it to the same inbox as the phone (POST /api/i18n/corrections).
 *
 * The page's text is read in the browser only, to see which catalogue
 * strings are on it; what is sent is the string's key, the language, the
 * catalogue's text for it (the template, `{placeholders}` unfilled), and
 * what the person typed. Never the page, and never a screenshot — a care
 * console's page is people's medicines.
 *
 * The consoles themselves are English today, so the page list is usually
 * empty and the search is how a word is found; it fills as their words are
 * translated.
 */
import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_LOCALE } from '@drbell/i18n/locales';
import { localeDirection } from '@drbell/i18n/direction';
import { displayTemplate, searchEntries, stringsOnPage, type WrongWordEntry } from './wrong-word.ts';

export interface WrongWordTransport {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
}

type Bundle = { strings: Record<string, string> };

const NOTE_MAX = 200;
const SUGGESTION_MAX = 500;

export function ReportWrongWordLink({
  api, locale, source, version,
}: {
  api: WrongWordTransport;
  /** The signed-in person's language; nothing is shown for English or before it is known. */
  locale: string | null | undefined;
  source: 'care' | 'org';
  version?: string;
}) {
  const [open, setOpen] = useState<{ pageText: string } | null>(null);
  if (!locale || locale === DEFAULT_LOCALE) return null;
  return (
    <>
      <button
        type="button"
        // The page as it is now, before the dialog covers it.
        onClick={() => setOpen({ pageText: document.body.innerText })}
        className="text-xs text-(--muted) underline underline-offset-2 hover:text-(--accent)"
      >
        Report a wrong word
      </button>
      {open && (
        <ReportDialog
          api={api}
          locale={locale}
          source={source}
          version={version}
          pageText={open.pageText}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function ReportDialog({
  api, locale, source, version, pageText, onClose,
}: {
  api: WrongWordTransport;
  locale: string;
  source: 'care' | 'org';
  version?: string;
  pageText: string;
  onClose: () => void;
}) {
  const [entries, setEntries] = useState<WrongWordEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<WrongWordEntry | null>(null);
  const [suggestion, setSuggestion] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // The language's strings and the English, once, when the dialog opens.
  useEffect(() => {
    let live = true;
    Promise.all([
      api.get<Bundle>(`/api/i18n/bundles/${encodeURIComponent(locale)}`),
      api.get<Bundle>(`/api/i18n/bundles/${DEFAULT_LOCALE}`),
    ])
      .then(([mine, english]) => {
        if (!live) return;
        setEntries(
          Object.entries(mine.strings)
            // Not yet translated: English shown, so nothing to report.
            .filter(([key, text]) => english.strings[key] !== undefined && english.strings[key] !== text)
            .map(([key, text]) => ({ key, text, english: english.strings[key]! })),
        );
      })
      .catch((e) => live && setLoadError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [api, locale]);

  const onPage = useMemo(() => (entries ? stringsOnPage(entries, pageText) : []), [entries, pageText]);
  const found = useMemo(() => (entries ? searchEntries(entries, query) : []), [entries, query]);
  const shown = query.trim() ? found : onPage;

  const send = async () => {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/i18n/corrections', {
        key: chosen.key,
        locale,
        currentText: chosen.text,
        suggestion: suggestion.trim() || null,
        note: note.trim() || null,
        source,
        appVersion: version ?? null,
      });
      setSent(true);
    } catch (e) {
      const status = (e as { status?: number }).status;
      setError(status === 429
        ? 'You have sent many reports today. Please try again tomorrow.'
        : `Could not send it: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const input = 'mt-1 w-full rounded-lg border border-(--line) bg-(--bg) px-3 py-2 text-sm outline-none focus:border-(--accent)';

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Report a wrong word"
    >
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 className="text-lg font-semibold">Report a wrong word</h2>

        {sent ? (
          <>
            <p className="mt-3 text-sm">Thank you. A person at DrBell will check it and fix it for everyone.</p>
            <div className="mt-5 flex justify-end">
              <button onClick={onClose} className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white">
                Done
              </button>
            </div>
          </>
        ) : chosen ? (
          <div className="mt-3 space-y-3 overflow-y-auto">
            <div className="rounded-lg border border-(--accent) p-3 text-sm">
              <p className="text-xs text-(--muted)">The words you chose</p>
              <p lang={locale} dir={localeDirection(locale)}>{displayTemplate(chosen.text)}</p>
              <p className="mt-2 text-xs text-(--muted)">In English</p>
              <p className="text-xs text-(--muted)">{displayTemplate(chosen.english)}</p>
            </div>
            <label className="block text-sm">
              Better words, if you know them
              <textarea
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                maxLength={SUGGESTION_MAX}
                lang={locale} dir={localeDirection(locale)}
                rows={2}
                className={input}
              />
            </label>
            <label className="block text-sm">
              Anything else? (optional)
              <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={NOTE_MAX} rows={2} className={input} />
            </label>
            <p className="text-xs text-(--muted)">
              Please do not write names, medicines or anything about health. No screenshot is sent.
            </p>
            {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                onClick={() => setChosen(null)}
                disabled={busy}
                className="min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)"
              >
                Choose different words
              </button>
              <button
                onClick={() => void send()}
                disabled={busy}
                className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
              >
                {busy ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mt-1 text-sm text-(--muted)">Pick the words that look wrong.</p>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search all the words"
              aria-label="Search all the words"
              className={`${input} min-h-11`}
            />
            <div className="mt-3 min-h-0 flex-1 space-y-2 overflow-y-auto">
              {loadError && <p className="text-sm text-red-500">Could not load the words: {loadError}</p>}
              {!entries && !loadError && <p className="text-sm text-(--muted)">Loading…</p>}
              {entries && shown.length === 0 && (
                <p className="text-sm text-(--muted)">
                  {query.trim() ? 'No words match.' : 'None of the translated words are on this page. Search for the one you saw.'}
                </p>
              )}
              {shown.map((e) => (
                <button
                  key={e.key}
                  onClick={() => setChosen(e)}
                  className="block w-full rounded-lg border border-(--line) px-3 py-2 text-left text-sm hover:border-(--accent)"
                >
                  <span lang={locale} dir={localeDirection(locale)}>{displayTemplate(e.text)}</span>
                  <span className="block text-xs text-(--muted)">{displayTemplate(e.english)}</span>
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)">
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
